import axios, { AxiosResponse } from 'axios';
import httpClient from '@/lib/api/http/client';
import {
  IFilterLocation,
  IOutletAllList,
  IProductType,
} from '@/src/models/produk-kesehatan';
import { IApiResponse } from '@/src/models/shared/common';
import { safeParseZodObject } from '@/src/shared/utils/parser';
import {
  mapConsultListServiceToConsultListServiceRequest,
  mapOutletListServiceToOutletListServiceRequest,
  mapProductListServiceToProductListServiceRequest,
} from '../data/mapper';
import {
  SProductDetailResponse,
  TOutletListService,
  TProductDetailResponse,
} from '../data/schemas';
import {
  OutletListNotFoundError,
  ProductListNotFoundError,
  ProductTypeNotFoundError,
  ProvinceNotFoundError,
  ServiceListNotFoundError,
} from '../error';

export interface IFilterParams {
  offset: number | undefined;
  size: number | undefined;
  search?: string | null;
  lat: number | undefined;
  long: number | undefined;
  min_price?: number | null;
  max_price?: number | null;
  lokasi?: string[] | null | undefined;
  order_by: string | undefined;
  service_method?: string[] | null;
  payment_method?: string[] | null;
  min_date?: string | null;
  max_date?: string | null;
  is_not_ethical?: boolean | null;
}

export interface IGetOutletListParams {
  body: TOutletListService;
  page: number;
  per_page_size: number;
}

export interface IGetProvinceParams {
  page: number;
  per_page_size: number;
  longitude?: number;
  latitude?: number;
}

export interface IGetProductDetailParams {
  id: number | string;
}

export interface IGetProductTypeParams {
  page: number;
  per_page_size: number;
}

export interface IGetServiceParams {
  bpis_id: number | string;
  individual_id: number | string;
}

export const outletListService = {
  getOutletList: async ({
    body,
    page,
    per_page_size,
  }: IGetOutletListParams) => {
    const request = mapOutletListServiceToOutletListServiceRequest(body);

    try {
      const response: AxiosResponse<IOutletAllList> =
        await httpClient.healthcareIntelligence.post(
          `/marketplacev2/post-list-master-outlet-marketplace`,
          request,
          {
            params: { page, per_page_size },
          }
        );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new OutletListNotFoundError();
      }

      throw error;
    }
  },

  getProvince: async ({
    page,
    per_page_size,
    longitude,
    latitude,
  }: IGetProvinceParams) => {
    const params = { page, per_page_size, longitude, latitude };
    try {
      const response: AxiosResponse<IFilterLocation> =
        await httpClient.healthcareIntelligence.get(
          '/marketplacev2/get-filter-location',
          { params }
        );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new ProvinceNotFoundError();
      }

      throw error;
    }
  },

  getProductType: async ({ page, per_page_size }: IGetProductTypeParams) => {
    const params = { page, per_page_size };

    try {
      const response: AxiosResponse<IProductType> =
        await httpClient.healthcareIntelligence.get(
          '/marketplacev2/get-master-product-type',
          { params }
        );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new ProductTypeNotFoundError();
      }

      throw error;
    }
  },
};

export const productService = {
  getMarketplaceProductList: async ({
    offset,
    size,
    search,
    lat,
    long,
    min_price,
    max_price,
    lokasi,
    order_by,
    is_not_ethical,
  }: IFilterParams) => {
    const request = mapProductListServiceToProductListServiceRequest({
      offset,
      size,
      search,
      latitude: lat,
      longitude: long,
      min_price,
      max_price,
      lokasi,
      order_by,
      is_not_ethical,
    });

    try {
      const response = await httpClient.healthcareIntelligence.post(
        `/marketplace/get-marketplace-product`,
        request
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new ProductListNotFoundError();
      }

      throw error;
    }
  },

  getMarketplaceProductDetail: async ({ id }: IGetProductDetailParams) => {
    const params = {
      id,
    };

    const response: AxiosResponse<IApiResponse<TProductDetailResponse>> =
      await httpClient.healthcareIntelligence.post(
        '/marketplace/get-marketplace-product',
        {},
        { params }
      );
    const validated = safeParseZodObject<TProductDetailResponse>(
      SProductDetailResponse,
      response.data.data
    );
    return validated ?? (response.data.data as TProductDetailResponse);
  },
};

export const consultService = {
  getMarketplaceServiceList: async ({
    offset,
    size,
    search,
    lat,
    long,
    min_price,
    max_price,
    lokasi,
    order_by,
    service_method,
    payment_method,
    min_date,
    max_date,
  }: IFilterParams) => {
    const request = mapConsultListServiceToConsultListServiceRequest({
      offset,
      size,
      search,
      latitude: lat,
      longitude: long,
      min_price,
      max_price,
      lokasi,
      order_by,
      service_method,
      payment_method,
      min_date,
      max_date,
    });

    try {
      const response = await httpClient.healthcareIntelligence.post(
        '/marketplace/get-marketplace-service',
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new ServiceListNotFoundError();
      }

      throw error;
    }
  },

  getMarketplaceServiceDetail: async ({
    bpis_id,
    individual_id,
  }: IGetServiceParams) => {
    const params = {
      bpis_id,
      individual_id,
    };

    try {
      const response = await httpClient.healthcareIntelligence.post(
        '/marketplace/get-marketplace-service',
        { params }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new ServiceListNotFoundError();
      }

      throw error;
    }
  },
};
